<template>
  <div id="build" :class="{'build-default': !isResizedDesktop, 'build-resize': isResizedDesktop}">
    <navbar
      :startBuild="startBuild"
      :screenNumber="screenNumber"
      :previewScreen="previewScreen"
      :activeColumn="activeColumn"
      v-on:next="nextScreen"
      v-on:previous="previousScreen"
      v-on:preview="doPreview"
      v-on:goback="goBack"
      v-on:startOver="startOver"
      v-on:openDialog="openDialog"
    ></navbar>
    <v-container
      class="intro-block"
      v-if="startBuild === false || screenNumber === 1"
    >
      <v-layout row align-center fill-height>
        <v-flex xs12 sm10 offset-sm1 offset-md0 offset-lg1 md8>
          <h1>
            <span class="light">Dealer </span
            ><span class="heavy">Draft</span> Homepage Builder
          </h1>
          <p>
            Every great site is built with tailor made components. Select which components best suit your new site's needs. Just Build, Preview and take a screenshot of your new site with our personalized website builder.
          </p>
          <p>
            Note: images, colors, and brands are flexible with each component and can be tailored to your brand.
          </p>
          <v-form>
            <v-layout row>
              <v-flex xs8 sm7 md7 lg6>
                <v-select
                  :items="brands"
                  clearable
                  attach
                  label="Choose Your Brand"
                  v-model="select.chosenBrand"
                ></v-select>
                <template v-if="!hasBrandedSite">
                  <v-text-field 
                    append-icon="attach_file"
                    accept="image/png, image/jpeg"
                    label="Upload Your Logo"
                    @click='onPickFile'
                    v-model='logoFileName'
                    :rules="rules"
                  ></v-text-field>
                  <v-img class="logo-preview" :class="showPreviewLogo" :src="logoURL" alt="User Logo"></v-img>
                  <input
                    type="file"
                    style="display: none"
                    ref="fileInput"
                    accept="image/png, image/jpeg"
                    @change="onFilePicked">
                </template>
                <v-btn depressed @click="submitForm">{{ hasBrandedSite ? 'Continue' : 'Start Building' }}</v-btn>
              </v-flex>
            </v-layout>
          </v-form>
        </v-flex>
        <v-flex hidden-sm-and-down md3 offset-md1 lg2 justify-center fill-height mt-5>
          <div class="component-visual">
            <div class="visual-box">
              <span>NAV</span>
            </div>
            <h4>Navigation Components</h4>
          </div>
          <div class="component-visual">
            <div class="visual-box">
              <span>HP</span>
            </div>
            <h4>Homepage Components</h4>
          </div>
          <div class="component-visual">
            <div class="visual-box">
              <span>F</span>
            </div>
            <h4>Footer Components</h4>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container fluid v-else-if="previewScreen === true">
      <preview-page :screenNumber="screenNumber" :logoURL="logoURL"></preview-page>
      <v-dialog v-model="linkDialog" lazy max-width="500px">
        <link-modal v-on:closing="linkDialog = false"></link-modal>
      </v-dialog>
    </v-container>
    <v-container fluid v-else>
      <v-layout :class="{ 'row mobile-tab-layout': isMobile600, 'row': !isMobile600 }">
        <div v-if="!isMobile600">
          <v-flex sm9>
            <component-adder :screenNumber="screenNumber"></component-adder>
          </v-flex>
          <v-flex sm3 style="position:relative;">
            <side-preview></side-preview>
          </v-flex>
        </div>
        <div v-else class="mobile-container">
          <v-btn-toggle v-model="activeColumn" dense class="toggle-buttons">
            <v-btn :value="1" class="toggle-btn">Components</v-btn>
            <v-btn :value="2" class="toggle-btn">Layout</v-btn>
          </v-btn-toggle>
            <div v-if="activeColumn === 1" class="column">
              <component-adder :screenNumber="screenNumber"></component-adder>
            </div>
            <div v-if="activeColumn === 2" class="column">
              <side-preview></side-preview>
            </div>
        </div>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import BuilderNav from "../components/BuilderNav.vue";
import ComponentAdder from "../components/ComponentAdder.vue";
import SidePreview from "../components/SidePreview.vue";
import PreviewPage from "../components/PreviewPage.vue";
import LinkModal from "../components/LinkModal.vue";
import screenMixin from "../mixins/screenMixin";
import { BRANDS, getBrandedSiteUrl } from "../config/brands";

export default {
  mixins: [screenMixin],
  data() {
    return {
      startBuild: false,
      select: { chosenBrand: "None" },
      brands: BRANDS,
      screenNumber: 1,
      previewScreen: false,
      logoFileName: '',
      logoURL: '',
      logoFileObject: null,
      showPreviewLogo: 'hidden',
      rules: [
        v => !!v || (v && v.size > 1000000) || 'Logo size should be less than 800 KB!',
      ],
      linkDialog: false,
      activeColumn: 1,
    };
  },
  mounted () {
    window.scrollTo(0, 0);
    document.getElementById('app').scrollIntoView();
  },
  computed: {
    brandedSiteUrl() {
      return getBrandedSiteUrl(this.select.chosenBrand);
    },
    hasBrandedSite() {
      return !!this.brandedSiteUrl;
    }
  },
  methods: {
    submitForm() {
      if (this.brandedSiteUrl) {
        window.open(this.brandedSiteUrl, '_blank');
        return;
      }
      this.startBuild = true;
      this.screenNumber = 2;
      if (this.select.chosenBrand === undefined) {
        this.$store.commit("chooseBrand", "None");
      } else {
        this.$store.commit("chooseBrand", this.select.chosenBrand.toLowerCase());
      }
    },
    nextScreen() {
      this.screenNumber = this.screenNumber + 1;
      window.scrollTo(0, 0);
      document.getElementById('app').scrollIntoView();
    },
    previousScreen() {
      this.screenNumber = this.screenNumber - 1;
      window.scrollTo(0, 0);
      document.getElementById('app').scrollIntoView();
    },
    doPreview() {
      this.previewScreen = true;
    },
    goBack() {
      this.previewScreen = false;
    },
    startOver() {
      this.startBuild = false;
      this.screenNumber = 1;
      this.$store.state.chosenComponents = [];
      this.previewScreen = false;
      this.select.chosenBrand = "None";
      this.$store.commit("chooseBrand", "None");
      this.clearLogo();
      this.activeColumn = 1;
    },
    onPickFile () {
      this.$refs.fileInput.click()
    },
    onFilePicked (event) {
      const files = event.target.files
      if (files[0] !== undefined && files[0].size < 1000000) {
        this.logoFileName = files[0].name
        // Check validity of file
        if (this.logoFileName.lastIndexOf('.') <= 0) {
          return false
        }
        // If valid, continue
        const fr = new FileReader()
        fr.readAsDataURL(files[0])
        fr.addEventListener('load', () => {
          this.logoURL = fr.result
          this.logoFileObject = files[0] // this is an file that can be sent to server...
          this.showPreviewLogo = 'show'
        })
      } else {
        this.clearLogo()
      }
    },
    clearLogo () {
      this.showPreviewLogo = 'hidden';
      this.logoFileName = ''
      this.logoFileObject = null
      this.logoURL = ''
    },
    openDialog () {
      this.linkDialog = true;
    },
  },
  watch: {
    screenNumber: function () {
      window.scrollTo(0,0)
    },
    previewScreen: function () {
      window.scrollTo(0,0)
    },
    isResizedBuilderLayout(newVal) {
      if (newVal) {
        this.activeColumn = 1;
      }
    }
  },
  components: {
    navbar: BuilderNav,
    componentAdder: ComponentAdder,
    sidePreview: SidePreview,
    previewPage: PreviewPage,
    linkModal: LinkModal
  },
};
</script>

<style>
#build {
  min-height: calc(100vh - 36px);
}
#build .container.fluid {
    padding-top: 0;
}
.clear-btn {
  margin: 0;
}
#build .intro-block h1 {
  margin: 30px 0px 15px;
  font-weight: 500;
  font-size: 3.5rem;
}
#build .intro-block p {
  font-size: 16px;
}
#build .light {
  font-weight: 300;
}
#build .heavy {
  font-weight: 600;
}
#build .intro-block span {
  color: #006aff;
  font-family: futura-pt, "Avenir", Helvetica, Arial, sans-serif;
}
#build .intro-block button {
  margin-left: 0px;
  margin-top: 30px;
  background: #006aff;
  color: #fff;
  border: 2px solid #006aff;
  font-size: 16px;
  font-weight: 400;
  text-transform: capitalize;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}
#build .intro-block button:hover {
  background: #fff;
  color: #006aff;
}
#build .v-btn--active:before,
#build .v-btn:hover:before,
#build .v-btn:focus:before {
  background-color: #fff;
}

#build .intro-block .v-select {
  margin-top: 30px;
  padding-top: 0;
}
#build .v-menu__content {
  z-index: 10000 !important;
}
.hidden {
  display: none;
}
.mobile-tab-layout {
  justify-content: center;
}
.mobile-tab-layout .mobile-container .column {
  flex: 1;
  padding: 20px;
}
.mobile-tab-layout .mobile-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mobile-tab-layout .mobile-container .toggle-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
  background: #f5f5f5;
}

.mobile-tab-layout .mobile-container .toggle-btn {
  min-width: 100px;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px;
}

.mobile-tab-layout .mobile-container .toggle-btn.v-btn--active {
  background-color: #1565C0 !important;
  color: #ffffff;
  pointer-events: none;
}
.mobile-tab-layout .mobile-container .previews .component-section .pr-5 {
  padding-right: 16px !important;
}

/* ADJUST FOR EXTENDED NAV WITHOUT DD BAR ACTIVE */
body.preview-page #preview:has(#nav-right-with-contact-v-extended-toolbar) nav.navbar.navbar-inverse.navbar-fixed-top,
body.preview-page #preview:has(#nav-right-with-contact-v-extended-toolbar) .click-through-mobile-contact-bar.navbar.navbar-fixed-top {
  top: 0 !important;
}

</style>

<style scoped>
.visual-box {
  height: 60px;
  width: 60px;
  border: 2px solid #006aff;
  border-radius: 4px;
  margin: auto auto 20px;
  display: flex;
  color: #006aff;
  font-weight: 600;
  align-items: center;
  justify-content: center;
}
.component-visual {
  color: #006aff;
  margin-bottom: 30px;
  text-align: center;
}

@media (min-width: 960px) {
    .build-default #build-nav {
      height: 64px;
    }
  }
  @media (min-width: 583px) and (max-width: 959px) {
    .build-default #build-nav {
      height: 48px;
    }
  }
  @media (max-width: 582px) {
    .build-default #build-nav {
      height: 56px;
    }
  }

  @media (min-width: 960px) {
    .build-resize #build-nav {
      height: 64px;
    }
  }
  @media (min-width: 769px) and (max-width: 959px) {
    .build-resize #build-nav {
      height: 48px;
    }
  }
  @media (max-width: 768px) {
    .build-resize #build-nav {
      height: 56px;
    }
  }
</style>
